import { useState, useEffect, useCallback } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell, BarChart, Bar, CartesianGrid,
} from "recharts";
import "./AdminDashboard.css";

const API = process.env.NODE_ENV === "production"
  ? window.location.origin
  : (process.env.REACT_APP_API_URL || "http://localhost:5000");

const GOLD = "#c9b896";
const MUTED = "rgba(236,229,211,0.5)";
const PIE_COLORS = ["#c9b896", "#3d6b3a", "#f0a04b", "#8b9a7a", "#7a6b50", "#5a7a65"];

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("es_token")}` };
}

function badge(status) {
  return <span className={`ad-badge ad-badge-${status}`}>{status}</span>;
}

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="ad-chart-tip">
      {label && <div className="ad-chart-tip-label">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || GOLD, fontSize: 13 }}>
          {p.name ? `${p.name}: ` : ""}{p.value}
        </div>
      ))}
    </div>
  );
}

/* ── Date range helpers ── */
const DATE_RANGE_OPTS = [
  { value: "today",     label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "7d",        label: "Last 7 days" },
  { value: "30d",       label: "Last 30 days" },
  { value: "90d",       label: "Last 90 days" },
  { value: "all",       label: "All time" },
];

function getDateBounds(range) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (range) {
    case "today":     return { start: today, end: now };
    case "yesterday": { const y = new Date(today); y.setDate(y.getDate() - 1); return { start: y, end: today }; }
    case "7d":  return { start: new Date(now - 7  * 86400000), end: now };
    case "30d": return { start: new Date(now - 30 * 86400000), end: now };
    case "90d": return { start: new Date(now - 90 * 86400000), end: now };
    default:    return { start: new Date(0), end: now };
  }
}

function filterSubsByDate(subs, range) {
  if (range === "all") return subs;
  const { start, end } = getDateBounds(range);
  return subs.filter((s) => { const t = new Date(s.createdAt); return t >= start && t <= end; });
}

/* ── Analytics builders ── */
function buildTimeData(subs, range) {
  if (range === "today" || range === "yesterday") {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const base = range === "today" ? today : new Date(today.getTime() - 86400000);
    const map = {};
    for (let h = 0; h < 24; h++) map[`${String(h).padStart(2, "0")}:00`] = 0;
    subs.forEach((s) => {
      const d = new Date(s.createdAt);
      if (d >= base && d < new Date(base.getTime() + 86400000)) {
        const lbl = `${String(d.getHours()).padStart(2, "0")}:00`;
        if (map[lbl] !== undefined) map[lbl]++;
      }
    });
    return Object.entries(map).map(([date, count]) => ({ date, count }));
  }
  const days = range === "7d" ? 7 : range === "90d" ? 90 : 30;
  const map = {};
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i);
    const k = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
    map[k] = 0;
  }
  subs.forEach((s) => {
    const k = new Date(s.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
    if (map[k] !== undefined) map[k]++;
  });
  return Object.entries(map).map(([date, count]) => ({ date, count }));
}

function buildStatusData(subs) {
  const m = { new: 0, reviewed: 0, accepted: 0, declined: 0 };
  subs.forEach((s) => { if (m[s.status] !== undefined) m[s.status]++; });
  return Object.entries(m).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }));
}

function buildCraftData(subs) {
  const m = {};
  subs.forEach((s) => { (s.crafts || []).forEach((c) => { m[c] = (m[c] || 0) + 1; }); });
  return Object.entries(m).sort((a, b) => b[1] - a[1]).slice(0, 7)
    .map(([name, value]) => ({ name: name.replace("-", " "), value }));
}

function buildCityData(subs) {
  const m = {};
  subs.forEach((s) => { if (!s.city) return; const c = s.city.split(",")[0].trim(); m[c] = (m[c] || 0) + 1; });
  return Object.entries(m).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([name, value]) => ({ name, value }));
}

function buildFragileData(subs) {
  const m = { Fragile: 0, "Non-fragile": 0 };
  subs.forEach((s) => {
    if (!s.fragile) return;
    const v = s.fragile.toLowerCase();
    if (v.startsWith("yes")) m.Fragile++;
    else if (v.startsWith("no")) m["Non-fragile"]++;
  });
  return Object.entries(m).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }));
}

function buildRestockData(subs) {
  const m = {};
  subs.forEach((s) => {
    if (!s.restock) return;
    m[s.restock] = (m[s.restock] || 0) + 1;
  });
  return Object.entries(m).map(([name, value]) => ({ name, value }));
}

/* ── Donut chart ── */
function DonutCard({ title, data }) {
  if (!data.length) return null;
  return (
    <div className="ad-chart-card">
      <div className="ad-chart-title">{title}</div>
      <ResponsiveContainer width="100%" height={160}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={44} outerRadius={66} dataKey="value" paddingAngle={3}>
            {data.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="none" />)}
          </Pie>
          <Tooltip content={<ChartTip />} />
        </PieChart>
      </ResponsiveContainer>
      <div className="ad-chart-legend">
        {data.map((d, i) => (
          <div key={i} className="ad-legend-row">
            <span className="ad-legend-dot" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
            <span className="ad-legend-label">{d.name}</span>
            <span className="ad-legend-val">{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Horizontal bar chart ── */
function BarCard({ title, data }) {
  if (!data.length) return null;
  return (
    <div className="ad-chart-card">
      <div className="ad-chart-title">{title}</div>
      <ResponsiveContainer width="100%" height={Math.max(120, data.length * 34)}>
        <BarChart data={data} layout="vertical" margin={{ top: 0, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(236,229,211,0.04)" horizontal={false} />
          <XAxis type="number" tick={{ fill: MUTED, fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
          <YAxis type="category" dataKey="name" tick={{ fill: MUTED, fontSize: 11 }} axisLine={false} tickLine={false} width={108} />
          <Tooltip content={<ChartTip />} cursor={{ fill: "rgba(201,168,64,0.05)" }} />
          <Bar dataKey="value" fill={GOLD} radius={[0, 3, 3, 0]} barSize={14} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ── View field ── */
function VF({ label, val, wide }) {
  if (!val || val === "—") return null;
  return (
    <div className={`ad-vf${wide ? " ad-vf-wide" : ""}`}>
      <div className="ad-vf-label">{label}</div>
      <div className="ad-vf-val">{val}</div>
    </div>
  );
}

/* ══════════════════════════════════════════ MAIN COMPONENT ══ */
export default function AdminDashboard() {
  const [adminEmail, setAdminEmail]     = useState("");
  const [submissions, setSubmissions]   = useState([]);
  const [pageStats, setPageStats]       = useState({});
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage]                 = useState("dashboard");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [viewTarget, setViewTarget]     = useState(null);
  const [lightboxSrc, setLightboxSrc]   = useState(null);
  const [editTarget, setEditTarget]     = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving]             = useState(false);
  const [dateRange, setDateRange]       = useState("all");

  const logout = () => { localStorage.removeItem("es_token"); window.location.href = "/admin"; };

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevHtmlHeight = html.style.height;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyHeight = body.style.height;
    html.style.overflow = "auto";
    html.style.height = "auto";
    body.style.overflow = "auto";
    body.style.height = "auto";
    return () => {
      html.style.overflow = prevHtmlOverflow;
      html.style.height = prevHtmlHeight;
      body.style.overflow = prevBodyOverflow;
      body.style.height = prevBodyHeight;
    };
  }, []);

  const loadSubmissions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/submissions`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return; }
      setSubmissions(await res.json());
    } catch {}
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  const loadStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/stats`, { headers: authHeaders() });
      if (res.ok) setPageStats(await res.json());
    } catch {}
  }, []); // eslint-disable-line

  useEffect(() => {
    const token = localStorage.getItem("es_token");
    if (!token) { window.location.href = "/admin"; return; }
    fetch(`${API}/api/auth/me`, { headers: authHeaders() })
      .then((r) => { if (r.status === 401) logout(); return r.json(); })
      .then((d) => setAdminEmail(d.email || ""))
      .catch(() => {});
    loadSubmissions();
    loadStats();
  }, [loadSubmissions, loadStats]);

  /* ── Analytics ── */
  const filteredSubs = filterSubsByDate(submissions, dateRange);
  const total        = filteredSubs.length;
  const newToday     = submissions.filter((s) => new Date(s.createdAt) > Date.now() - 86400000).length;
  const accepted     = filteredSubs.filter((s) => s.status === "accepted").length;

  /* Traffic KPIs */
  const applyVisits  = pageStats.apply_visit || 0;
  const igClicks     = pageStats.ig_click    || 0;
  const ttClicks     = pageStats.tt_click    || 0;
  const conversion   = applyVisits > 0 ? Math.round((total / applyVisits) * 100) : 0;

  /* Submission insight KPIs */
  const fragileCount    = filteredSubs.filter((s) => (s.fragile || "").toLowerCase().startsWith("yes")).length;
  const nonFragile      = filteredSubs.filter((s) => (s.fragile || "").toLowerCase().startsWith("no")).length;
  const withIgLink      = filteredSubs.filter((s) => (s.link || "").toLowerCase().includes("instagram")).length;
  const sharedLink      = filteredSubs.filter((s) => (s.link || "").trim()).length;
  const foodCount       = filteredSubs.filter((s) => (s.crafts || []).includes("food-beverage")).length;
  const canRestock      = filteredSubs.filter((s) => (s.restock || "").toLowerCase().includes("easily")).length;

  const timeData    = buildTimeData(filteredSubs, dateRange);
  const statusData  = buildStatusData(filteredSubs);
  const craftData   = buildCraftData(filteredSubs);
  const cityData    = buildCityData(filteredSubs);
  const fragileData = buildFragileData(filteredSubs);
  const restockData = buildRestockData(filteredSubs);

  /* ── Filtered table ── */
  const q = search.toLowerCase();
  const tableSubs = filterSubsByDate(submissions, dateRange);
  const visible = tableSubs.filter((s) => {
    const matchStatus = statusFilter === "all" || s.status === statusFilter;
    const matchSearch = !q || [s.name, s.city, s.whatsapp, s.email].some((f) => (f || "").toLowerCase().includes(q));
    return matchStatus && matchSearch;
  });

  /* ── Edit ── */
  async function saveEdit(e) {
    e.preventDefault(); setSaving(true);
    const els = e.target.elements;
    try {
      const res = await fetch(`${API}/api/submissions/${editTarget._id}`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          status: els.status.value, notes: els.notes.value,
          name: els.name.value, city: els.city.value,
          whatsapp: els.whatsapp.value, email: els.email.value,
        }),
      });
      if (res.ok) { setEditTarget(null); loadSubmissions(); }
    } finally { setSaving(false); }
  }

  async function confirmDelete() {
    try {
      await fetch(`${API}/api/submissions/${deleteTarget._id}`, { method: "DELETE", headers: authHeaders() });
      setDeleteTarget(null); loadSubmissions();
    } catch {}
  }

  const navItems = [
    { id: "dashboard",   label: "Dashboard" },
    { id: "submissions", label: "Submissions" },
    { id: "appearance",  label: "Appearance" },
    { id: "settings",    label: "Settings" },
  ];

  return (
    <div className="ad-root" onClick={() => mobileMenuOpen && setMobileMenuOpen(false)}>

      {/* ══ NAV ══ */}
      <nav className="ad-nav">
        <a className="ad-nav-logo" href="#top">
          <img src="/image/exyst-space-logo.svg" alt="exyst space"
            onError={(e) => { e.target.style.display = "none"; }} />
        </a>

        <div className={`ad-nav-items${mobileMenuOpen ? " open" : ""}`}>
          {navItems.map(({ id, label }) => (
            <button
              key={id}
              className={`ad-nav-item${page === id ? " active" : ""}`}
              onClick={(e) => { e.stopPropagation(); setPage(id); setMobileMenuOpen(false); }}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="ad-nav-right">
          <span className="ad-nav-email">{adminEmail}</span>
          <button className="ad-nav-logout" onClick={logout}>Log out</button>
          <button
            className="ad-nav-hamburger"
            onClick={(e) => { e.stopPropagation(); setMobileMenuOpen((v) => !v); }}
            aria-label="menu"
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* ══ MAIN ══ */}
      <main className="ad-main">

        {/* ── Controls bar ── */}
        <div className="ad-controls-bar">
          <div className="ad-page-title">
            {page === "dashboard"   && "Dashboard"}
            {page === "submissions" && "Submissions"}
            {page === "appearance"  && "Appearance"}
            {page === "settings"    && "Settings"}
          </div>
          <div className="ad-controls-right">
            <select
              className="ad-date-range-select"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
            >
              {DATE_RANGE_OPTS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button className="ad-refresh-btn" onClick={() => { loadSubmissions(); loadStats(); }}>
              Refresh
            </button>
          </div>
        </div>

        {/* ══ DASHBOARD PAGE ══ */}
        {page === "dashboard" && (
          <>
            {/* Traffic row */}
            <div className="ad-section-label">Traffic</div>
            <div className="ad-stat-grid">
              {[
                { label: "Submissions",      value: total,       sub: DATE_RANGE_OPTS.find(o => o.value === dateRange)?.label },
                { label: "New Today",        value: newToday,    sub: "last 24 h" },
                { label: "Apply Visits",     value: applyVisits, sub: "form page views" },
                { label: "Conversion",       value: applyVisits > 0 ? `${conversion}%` : "—", sub: "visit → submit" },
                { label: "Instagram Clicks", value: igClicks,    sub: "footer link" },
                { label: "TikTok Clicks",    value: ttClicks,    sub: "footer link" },
              ].map((c) => (
                <div className="ad-stat-card" key={c.label}>
                  <div className="ad-stat-label">{c.label}</div>
                  <div className="ad-stat-value">{loading ? "—" : c.value}</div>
                  <div className="ad-stat-sub">{c.sub}</div>
                </div>
              ))}
            </div>

            {/* Submission insight row */}
            <div className="ad-section-label">Submission insight</div>
            <div className="ad-stat-grid">
              {[
                { label: "Fragile items",      value: fragileCount, sub: `${nonFragile} non-fragile` },
                { label: "With Instagram",     value: withIgLink,   sub: "on social media" },
                { label: "Shared a link",      value: sharedLink,   sub: "have portfolio" },
                { label: "Food vendors",       value: foodCount,    sub: "F&B category" },
                { label: "Can restock easily", value: canRestock,   sub: "scalable supply" },
                { label: "Selected",           value: accepted,     sub: "accepted" },
              ].map((c) => (
                <div className="ad-stat-card" key={c.label}>
                  <div className="ad-stat-label">{c.label}</div>
                  <div className="ad-stat-value">{loading ? "—" : c.value}</div>
                  <div className="ad-stat-sub">{c.sub}</div>
                </div>
              ))}
            </div>

            {!loading && submissions.length > 0 && (
              <>
                <div className="ad-section-label" style={{ marginTop: 20 }}>Pipeline &amp; breakdowns</div>

                <div className="ad-charts-main">
                  <div className="ad-chart-card ad-chart-timeline">
                    <div className="ad-chart-title">
                      Submissions over time ({DATE_RANGE_OPTS.find(o => o.value === dateRange)?.label})
                    </div>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={timeData} margin={{ top: 8, right: 8, bottom: 0, left: -24 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(236,229,211,0.04)" />
                        <XAxis dataKey="date" tick={{ fill: MUTED, fontSize: 9 }} axisLine={false} tickLine={false}
                          interval={Math.floor(timeData.length / 6)} />
                        <YAxis tick={{ fill: MUTED, fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                        <Tooltip content={<ChartTip />} />
                        <Line type="monotone" dataKey="count" stroke={GOLD} strokeWidth={2}
                          dot={false} activeDot={{ r: 4, fill: GOLD, stroke: "#0a0906", strokeWidth: 2 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <DonutCard title="By status"        data={statusData} />
                </div>

                <div className="ad-charts-row">
                  <BarCard title="What they make"   data={craftData} />
                  <BarCard title="Where from (area)" data={cityData} />
                  <DonutCard title="Fragile vs non-fragile" data={fragileData} />
                </div>

                <div className="ad-charts-row">
                  <BarCard title="Can restock?" data={restockData} />
                </div>
              </>
            )}

            {!loading && submissions.length === 0 && (
              <div className="ad-empty-state">No submissions yet. They will appear here once the form goes live.</div>
            )}
          </>
        )}

        {/* ══ SUBMISSIONS PAGE ══ */}
        {page === "submissions" && (
          <>
            <div className="ad-sub-controls">
              <input className="ad-search" placeholder="Search name, city, WhatsApp..."
                value={search} onChange={(e) => setSearch(e.target.value)} />
              <select className="ad-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All statuses</option>
                <option value="new">New</option>
                <option value="reviewed">Reviewed</option>
                <option value="accepted">Accepted</option>
                <option value="declined">Declined</option>
              </select>
              <span className="ad-sub-count">{visible.length} shown · {submissions.length} total</span>
            </div>

            {loading ? (
              <div className="ad-loading">Loading submissions...</div>
            ) : (
              <div className="ad-table-wrap">
                <table className="ad-table">
                  <thead>
                    <tr>
                      <th>#</th><th>Name</th><th>City</th><th>Category</th>
                      <th>Status</th><th>Date</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.length === 0 ? (
                      <tr><td colSpan={7}><div className="ad-empty">No submissions found.</div></td></tr>
                    ) : visible.map((s, i) => (
                      <tr key={s._id} className="ad-table-row" onClick={() => setViewTarget(s)}>
                        <td className="ad-table-num">{i + 1}</td>
                        <td className="ad-table-name">
                          {s.name}
                          {(s.crafts || []).includes("food-beverage") && <span className="ad-fnb-tag">F&amp;B</span>}
                        </td>
                        <td className="ad-table-muted">{s.city}</td>
                        <td className="ad-table-muted ad-table-craft">
                          {(s.crafts || []).join(", ") || "—"}
                        </td>
                        <td>{badge(s.status)}</td>
                        <td className="ad-table-muted ad-table-date">{formatDate(s.createdAt)}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="ad-actions">
                            <button className="ad-btn-edit" onClick={() => setEditTarget(s)}>Edit</button>
                            <button className="ad-btn-del"  onClick={() => setDeleteTarget(s)}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ══ APPEARANCE PAGE ══ */}
        {page === "appearance" && (
          <div className="ad-placeholder">
            <div className="ad-placeholder-icon">◈</div>
            <div className="ad-placeholder-title">Appearance</div>
            <div className="ad-placeholder-sub">Theme and branding controls will live here.</div>
          </div>
        )}

        {/* ══ SETTINGS PAGE ══ */}
        {page === "settings" && (
          <div className="ad-placeholder">
            <div className="ad-placeholder-icon">⊙</div>
            <div className="ad-placeholder-title">Settings</div>
            <div className="ad-placeholder-sub">Configuration options will be available here.</div>
          </div>
        )}

      </main>

      {/* ══ LIGHTBOX ══ */}
      {lightboxSrc && (
        <div className="ad-lightbox" onClick={() => setLightboxSrc(null)}>
          <button className="ad-lightbox-close" onClick={() => setLightboxSrc(null)}>✕</button>
          <img src={lightboxSrc} alt="full size" onClick={(e) => e.stopPropagation()} className="ad-lightbox-img" />
        </div>
      )}

      {/* ══ VIEW PANEL ══ */}
      {viewTarget && (
        <>
          <div className="ad-panel-backdrop" onClick={() => setViewTarget(null)} />
          <div className="ad-panel">

            <div className="ad-panel-head">
              <div>
                <div className="ad-panel-name">{viewTarget.name}</div>
                <div className="ad-panel-meta">
                  {badge(viewTarget.status)}
                  <span className="ad-panel-city">{viewTarget.city}</span>
                  <span className="ad-panel-dot">·</span>
                  <span className="ad-panel-date">{formatDate(viewTarget.createdAt)}</span>
                  {(viewTarget.crafts || []).includes("food-beverage") && (
                    <span className="ad-panel-fnb">Food &amp; Beverage</span>
                  )}
                </div>
              </div>
              <button className="ad-panel-close" onClick={() => setViewTarget(null)}>✕</button>
            </div>

            <div className="ad-panel-contact">
              {[
                { label: "WhatsApp", val: viewTarget.whatsapp || "—" },
                { label: "Email",    val: viewTarget.email    || "—" },
                { label: "City",     val: viewTarget.city },
                { label: "Link",     val: viewTarget.link     || "—" },
              ].map(({ label, val }) => (
                <div key={label} className="ad-panel-contact-cell">
                  <div className="ad-panel-contact-label">{label}</div>
                  <div className="ad-panel-contact-val">{val}</div>
                </div>
              ))}
            </div>

            <div className="ad-panel-body">

              {!(viewTarget.crafts || []).includes("food-beverage") && (
                <div className="ad-panel-section">
                  <div className="ad-panel-section-title">Work</div>
                  <div className="ad-panel-grid">
                    <VF label="Category"       val={(viewTarget.crafts || []).join(", ") || "—"} />
                    <VF label="Quantity ready"  val={viewTarget.quantity} />
                    <VF label="Price range"     val={viewTarget.price} />
                    <VF label="Can restock"     val={viewTarget.restock} />
                  </div>
                  <VF label="Description" val={viewTarget.description} wide />
                  <VF label="Story"       val={viewTarget.story}       wide />
                </div>
              )}

              {(viewTarget.crafts || []).includes("food-beverage") && (
                <div className="ad-panel-section ad-panel-section-food">
                  <div className="ad-panel-section-title">Food &amp; Beverage</div>
                  <div className="ad-panel-grid">
                    <VF label="Offering"       val={viewTarget.foodOffering} />
                    <VF label="Made by"        val={viewTarget.foodMadeBy} />
                    <VF label="Fresh delivery" val={viewTarget.foodDeliveryFresh} />
                    <VF label="Storage"        val={viewTarget.foodStorage} />
                    <VF label="Stays fresh"    val={viewTarget.foodFreshDuration} />
                    <VF label="Price per item" val={viewTarget.foodPrice} />
                    <VF label="Packaging"      val={viewTarget.foodPackaging} />
                    <VF label="Certification"  val={viewTarget.foodCertification} />
                  </div>
                  <VF label="Product description" val={viewTarget.foodDescription}  wide />
                  <VF label="Delivery note"       val={viewTarget.foodDeliveryNote}  wide />
                  <VF label="Daily quantity"       val={viewTarget.foodDailyQty}      wide />
                  <VF label="Other notes"          val={viewTarget.foodOther}         wide />
                </div>
              )}

              <div className="ad-panel-section">
                <div className="ad-panel-section-title">Logistics</div>
                <div className="ad-panel-grid">
                  <VF label="Delivery"         val={viewTarget.delivery} />
                  <VF label="Fragile"          val={viewTarget.fragile} />
                  <VF label="Beyond the shelf" val={(viewTarget.beyond || []).join(", ")} />
                </div>
              </div>

              {viewTarget.notes && (
                <div className="ad-panel-section">
                  <div className="ad-panel-section-title">Admin Notes</div>
                  <div className="ad-panel-notes">{viewTarget.notes}</div>
                </div>
              )}

              {viewTarget.photos?.length > 0 && (
                <div className="ad-panel-section">
                  <div className="ad-panel-section-title">Photos ({viewTarget.photos.length})</div>
                  <div className="ad-panel-photos">
                    {viewTarget.photos.map((p, i) => (
                      <img key={i} src={`${API}${p}`} alt={`photo ${i + 1}`}
                        onClick={() => setLightboxSrc(`${API}${p}`)}
                        className="ad-panel-thumb" />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="ad-panel-footer">
              <button className="ad-panel-btn-ghost" onClick={() => setViewTarget(null)}>Close</button>
              <button className="ad-panel-btn-gold"  onClick={() => { setViewTarget(null); setEditTarget(viewTarget); }}>Edit</button>
            </div>
          </div>
        </>
      )}

      {/* ══ EDIT MODAL ══ */}
      {editTarget && (
        <div className="ad-overlay" onClick={(e) => e.target === e.currentTarget && setEditTarget(null)}>
          <div className="ad-modal">
            <div className="ad-modal-title">Edit Submission</div>
            <form className="ad-modal-body" onSubmit={saveEdit}>
              {[
                { label: "Status",      name: "status",   type: "select",
                  options: ["new","reviewed","accepted","declined"], def: editTarget.status },
                { label: "Admin Notes", name: "notes",    type: "textarea", def: editTarget.notes || "" },
                { label: "Full Name",   name: "name",     type: "input",    def: editTarget.name },
                { label: "City",        name: "city",     type: "input",    def: editTarget.city },
                { label: "WhatsApp",    name: "whatsapp", type: "input",    def: editTarget.whatsapp },
                { label: "Email",       name: "email",    type: "email",    def: editTarget.email || "" },
              ].map(({ label, name, type, options, def }) => (
                <div className="ad-modal-field" key={name}>
                  <label className="ad-modal-label">{label}</label>
                  {type === "select" ? (
                    <select className="ad-modal-select" name={name} defaultValue={def}>
                      {options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : type === "textarea" ? (
                    <textarea className="ad-modal-textarea" name={name} defaultValue={def} placeholder="Internal notes..." />
                  ) : (
                    <input className="ad-modal-input" name={name} type={type === "email" ? "email" : "text"} defaultValue={def} />
                  )}
                </div>
              ))}
              <div className="ad-modal-footer">
                <button type="button" className="ad-modal-cancel" onClick={() => setEditTarget(null)}>Cancel</button>
                <button type="submit" className="ad-modal-save" disabled={saving}>{saving ? "Saving..." : "Save"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══ DELETE MODAL ══ */}
      {deleteTarget && (
        <div className="ad-overlay" onClick={(e) => e.target === e.currentTarget && setDeleteTarget(null)}>
          <div className="ad-modal ad-modal-sm">
            <div className="ad-modal-title">Remove submission?</div>
            <p className="ad-del-text">
              Permanently deletes <strong>{deleteTarget.name}</strong>'s submission and photos. Cannot be undone.
            </p>
            <div className="ad-modal-footer">
              <button className="ad-modal-cancel" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="ad-modal-del"    onClick={confirmDelete}>Remove</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
